import { useMemo, useState } from 'react';
import { PLAYABLE_CHARACTERS, playableCharacterById, type PlayableCharacterId } from './render/AxieCharacter.ts';
import { firstLight, roomById } from './content/rooms/index.ts';
import { buildGroveHub } from './content/rooms/grove-hub.ts';
import { wardenTest } from './content/rooms/test/warden-test.ts';
import { GameView } from './ui/GameView.tsx';
import { loadCleared, loadSelectedCharacter, markCleared, saveSelectedCharacter } from './ui/progress.ts';
import { CharacterSelect } from './ui/CharacterSelect.tsx';
import type { RoomDef } from './sim/types.ts';

/**
 * Dev-only room switcher via ?room=<id>. Not part of ROOMS' own count (brief
 * §14's room budget is unaffected by this) - it exists so any authored room,
 * including a test room the Grove never lists, can be played by URL without
 * going through the hub first.
 *
 * Test rooms (like the Warden's) live here because they are never meant to
 * join ROOMS. Real, authored rooms resolve straight from ROOMS via roomById,
 * so this table never needs a new entry just because a room shipped.
 */
const DEV_ROOMS: Readonly<Record<string, RoomDef>> = {
  [wardenTest.id]: wardenTest,
  'warden-test': wardenTest,
};

function pickDevRoom(): RoomDef | null {
  const id = new URLSearchParams(window.location.search).get('room');
  if (!id) return null;
  if (DEV_ROOMS[id]) return DEV_ROOMS[id];
  try {
    return roomById(id);
  } catch {
    return firstLight;
  }
}

export default function App() {
  const devRoom = useMemo(() => pickDevRoom(), []);
  const [cleared, setCleared] = useState(loadCleared);
  const [selectedCharacterId, setSelectedCharacterId] = useState<PlayableCharacterId>(() => playableCharacterById(loadSelectedCharacter()).id);
  const [hasBegun, setHasBegun] = useState(false);
  // The Grove is a real, walkable room now (see `buildGroveHub`), not a
  // separate menu screen - there is always a `room`, never a null "show the
  // map instead" state.
  const [room, setRoom] = useState<RoomDef>(() => devRoom ?? buildGroveHub(cleared));

  const handleCleared = (roomId: string): void => {
    setCleared(markCleared(roomId));
  };

  const returnToHub = (): void => {
    // Rebuilt fresh every time: which doors are locked depends on `cleared`,
    // and a door's blocks are ordinary Seedblocks that don't outlive the
    // visit that cast them, same as any other room's don't survive a restart.
    setRoom(buildGroveHub(cleared));
  };

  const handleDoorEnter = (targetRoomId: string): void => {
    try {
      setRoom(roomById(targetRoomId));
    } catch {
      // Only reachable if a door's targetRoomId is wrong, which would be an
      // authoring bug in buildGroveHub, not something a player can trigger -
      // fail back to the hub rather than crash on it.
      returnToHub();
    }
  };

  const chooseCharacter = (id: PlayableCharacterId): void => {
    setSelectedCharacterId(id);
    saveSelectedCharacter(id);
  };

  if (!hasBegun) {
    return <CharacterSelect selectedId={selectedCharacterId} onSelect={chooseCharacter} onBegin={() => setHasBegun(true)} />;
  }

  const character = PLAYABLE_CHARACTERS.find((entry) => entry.id === selectedCharacterId) ?? PLAYABLE_CHARACTERS[0];

  return (
    <GameView
      key={room.id}
      room={room}
      onCleared={handleCleared}
      onReturnToMap={returnToHub}
      onDoorEnter={handleDoorEnter}
      brambleHornUnlocked={cleared.has('05-patrol')}
      character={character}
    />
  );
}
